import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock3, CreditCard, Download, MessageCircle, RefreshCw, Trash2 } from "lucide-react";
import { useRequests } from "@/contexts/RequestsContext";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { toWhatsAppHref, visibleContactItems } from "@/lib/contactSettings";
import { API_BASE } from "@/lib/api";

const STATUS = {
  nouveau: ["Demande reçue", "Nous vérifions votre demande."],
  envoyee: ["Demande reçue", "Nous vérifions votre demande."],
  en_cours: ["En traitement", "L’équipe Team Work prépare votre confirmation."],
  repondu: ["Confirmée", "Votre fiche officielle est disponible."],
  termine: ["Terminée", "Cette demande a été traitée."],
  terminee: ["Terminée", "Cette demande a été traitée."],
  annule: ["Annulée", "Cette demande a été annulée."],
};

const money = (value, currency = "USD") =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency, maximumFractionDigits: 2 }).format(Number(value || 0));

const titleFor = (item) => item.listing_title || item.service_name || item.project || item.request_type?.replaceAll("_", " ") || "Demande Team Work";
const isConfirmed = (item) => ["repondu", "termine", "terminee"].includes(item.status) || Boolean(item.confirmed_at);
const progressIndex = (item) => item.payment_status === "paid" ? 3 : isConfirmed(item) ? 2 : item.status === "en_cours" ? 1 : 0;

export default function Requests() {
  const { items, remove, refresh } = useRequests();
  const { settings } = useSiteSettings();
  const whatsapp = visibleContactItems(settings?.whatsapps, settings?.whatsapp, "WhatsApp")[0]?.value || "";
  const whatsappHref = toWhatsAppHref(whatsapp);

  const download = (item) => {
    const url = `${API_BASE}/requests/track/${encodeURIComponent(item.number)}/export?token=${encodeURIComponent(item.tracking_token)}&format=pdf`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!items.length) {
    return (
      <div className="container-app py-10">
        <EmptyState title="Aucune demande enregistrée" description="Vos réservations, visites, essais et devis apparaîtront ici sans créer de compte." />
        <div className="mt-6 text-center"><Button asChild><Link to="/">Explorer les univers <ArrowRight className="ml-2 h-4 w-4" /></Link></Button></div>
      </div>
    );
  }

  return (
    <div className="container-app max-w-5xl py-6 lg:py-12">
      <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-orange-600">Suivi Team Work Africa</p>
          <h1 className="mt-2 text-3xl font-black text-slate-950 lg:text-5xl">Mes demandes</h1>
          <p className="mt-2 text-slate-600">Confirmation, rendez-vous et paiement, mis à jour automatiquement.</p>
        </div>
        <Button variant="outline" onClick={refresh}><RefreshCw className="mr-2 h-4 w-4" />Actualiser</Button>
      </div>

      <div className="space-y-5">
        {items.map((item) => {
          const [label, description] = STATUS[item.status] || STATUS.nouveau;
          const step = progressIndex(item);
          const total = Number(item.total_due || 0);
          const paid = Number(item.paid_amount || 0);
          const balance = Math.max(0, Number(item.balance_due ?? total - paid));
          return (
            <article key={item.number} className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_55px_rgba(15,35,58,0.09)]">
              <div className="bg-[#0b1f3a] px-5 py-5 text-white sm:px-7">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400">Référence</p>
                    <h2 className="mt-1 text-xl font-black">{item.number}</h2>
                  </div>
                  <span className="rounded-full bg-white/10 px-4 py-2 text-sm font-bold">{label}</span>
                </div>
              </div>

              <div className="p-5 sm:p-7">
                <h3 className="text-xl font-black capitalize text-slate-950">{titleFor(item)}</h3>
                <p className="mt-1 text-sm text-slate-600">{description}</p>

                <div className="mt-6 grid grid-cols-4 gap-2">
                  {["Reçue", "Traitement", "Confirmée", "Payée"].map((name, index) => (
                    <div key={name} className="min-w-0">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-full ${index <= step ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-400"}`}>
                        {index < step ? <Check className="h-4 w-4" /> : index === step ? <Clock3 className="h-4 w-4" /> : <span className="text-xs font-black">{index + 1}</span>}
                      </div>
                      <p className="mt-2 truncate text-[10px] font-bold text-slate-600 sm:text-xs">{name}</p>
                    </div>
                  ))}
                </div>

                {(total > 0 || paid > 0) && (
                  <div className="mt-7 rounded-2xl bg-slate-50 p-4 sm:p-5">
                    <div className="mb-4 flex items-center gap-2 font-black text-slate-900"><CreditCard className="h-5 w-5 text-orange-600" />Résumé du paiement</div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><p className="text-xs text-slate-500">Total</p><p className="mt-1 font-black">{money(total, item.currency)}</p></div>
                      <div><p className="text-xs text-slate-500">Payé</p><p className="mt-1 font-black text-emerald-700">{money(paid, item.currency)}</p></div>
                      <div><p className="text-xs text-slate-500">Solde</p><p className="mt-1 font-black text-orange-700">{money(balance, item.currency)}</p></div>
                    </div>
                    <p className="mt-4 text-xs font-bold uppercase tracking-wide text-slate-500">
                      {item.payment_status === "paid" ? "Paiement confirmé" : item.payment_status === "partial" ? "Paiement partiel" : "Paiement en attente"}
                    </p>
                  </div>
                )}

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  {isConfirmed(item) && item.tracking_token ? (
                    <Button className="min-h-12 flex-1 bg-orange-600 hover:bg-orange-700" onClick={() => download(item)}>
                      <Download className="mr-2 h-4 w-4" />Télécharger ma fiche Team Work
                    </Button>
                  ) : (
                    <div className="flex min-h-12 flex-1 items-center justify-center rounded-xl bg-slate-100 px-4 text-center text-sm font-semibold text-slate-600">
                      La fiche sera disponible après confirmation de Team Work.
                    </div>
                  )}
                  {whatsappHref && (
                    <Button variant="outline" className="min-h-12" asChild>
                      <a href={whatsappHref} target="_blank" rel="noreferrer"><MessageCircle className="mr-2 h-4 w-4" />Nous écrire</a>
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" aria-label="Supprimer de mon appareil" onClick={() => remove(item.number)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
